import type { MouseEvent } from "react";

export interface ColumnResizeOptions {
  /** Minimum column width in pixels */
  minWidth?: number;
  /** Maximum column width in pixels */
  maxWidth?: number;
  /** Sample size for data cells to improve performance */
  sampleSize?: number;
  /** Handler for very long text content (length > 100 chars) */
  longContentWidth?: number;
}

const DEFAULT_OPTIONS: Required<ColumnResizeOptions> = {
  minWidth: 100,
  maxWidth: 600,
  sampleSize: 30,
  longContentWidth: 350,
};

/**
 * Calculates the optimal width for a table column based on its content
 * @param columnId The column identifier to find elements
 * @param tableId The unique table identifier to scope the search
 * @param options Configuration options for the resize calculation
 * @returns The calculated optimal width in pixels
 */
export function calculateColumnWidth(
  columnId: string,
  tableId: string,
  options: ColumnResizeOptions = {},
): number {
  const opts = { ...DEFAULT_OPTIONS, ...options };

  // Find all elements with this column ID within the specific table
  const tableContainer = document.querySelector(`[data-table-id="${tableId}"]`);
  const columnElements =
    tableContainer?.querySelectorAll(`[data-column-id="${columnId}"]`) || [];

  if (!columnElements.length) {
    return opts.minWidth;
  }

  let maxWidth = opts.minWidth;

  // Create a canvas for accurate text measurement
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  if (!context) {
    return opts.minWidth;
  }

  try {
    // Separate headers and cells
    const headerCells: Element[] = [];
    const dataCells: Element[] = [];

    columnElements.forEach((element) => {
      if (element.tagName === "TH") {
        headerCells.push(element);
      } else if (element.tagName === "TD") {
        dataCells.push(element);
      }
    });

    // Measure header cells
    headerCells.forEach((headerCell) => {
      const width = measureHeaderWidth(headerCell, context);
      if (width > 0) {
        maxWidth = Math.min(opts.maxWidth, Math.max(maxWidth, width));
      }
    });

    // Sample data cells efficiently
    const step = Math.max(1, Math.floor(dataCells.length / opts.sampleSize));

    for (let i = 0; i < dataCells.length; i += step) {
      const width = measureCellWidth(
        dataCells[i],
        context,
        opts.longContentWidth,
        opts.maxWidth,
      );
      if (width > 0) {
        maxWidth = Math.min(opts.maxWidth, Math.max(maxWidth, width));
      }
    }
  } catch (e) {
    console.error("Column measurement failed:", e);
    return opts.minWidth;
  }

  return maxWidth;
}

/**
 * Measures the width needed for a header cell including text and icons
 */
function measureHeaderWidth(
  headerCell: Element,
  context: CanvasRenderingContext2D,
): number {
  const titleElement = headerCell.querySelector(".line-clamp-2");
  if (!titleElement?.textContent) {
    return 0;
  }

  // Get computed styles for accurate measurement
  const computedStyle = window.getComputedStyle(titleElement);
  context.font = `${computedStyle.fontWeight} ${computedStyle.fontSize} ${computedStyle.fontFamily}`;

  const text = titleElement.textContent;
  const metrics = context.measureText(text);

  // Check for actually visible icons in this header
  // Icons are in the wrapper with "space-x-1" class
  const iconsWrapper = headerCell.querySelector(".space-x-1");
  const iconCount = iconsWrapper ? iconsWrapper.children.length : 0;

  // Each icon is 12px wide with 4px left margin
  // Icons have space-x-1 (4px) between them
  const iconSpace = iconCount > 0 ? iconCount * 16 + (iconCount - 1) * 4 : 0;
  const cellPadding = 16;
  const kebabSpace = 20; // Space for kebab menu

  return Math.ceil(metrics.width) + iconSpace + cellPadding + kebabSpace;
}

/**
 * Measures the width needed for a data cell
 */
function measureCellWidth(
  cell: Element,
  context: CanvasRenderingContext2D,
  longContentWidth: number,
  maxWidth: number,
): number {
  const contentEl = cell.querySelector(".cell-content");
  if (!contentEl) {
    return 0;
  }

  // Check if this cell contains non-text content (icons, etc.) or anchor tags
  const hasComplexContent =
    contentEl.querySelector("svg") ||
    contentEl.querySelector("img") ||
    contentEl.querySelector(".inline-flex") ||
    contentEl.querySelector(".flex") ||
    contentEl.querySelector("a"); // Include anchor tags in complex content

  if (hasComplexContent) {
    // For complex content, prioritize measuring the actual content elements
    // All of this is a bunch of gross hacks…maybe there is a better, more general solution in future
    let bestWidth = 0;

    // Get cell ID for special handling
    const cellId = cell.getAttribute("data-column-id");

    // Special handling for Channel, Status, and People cells in touchpoint tables
    // These have specific structures with dots, icons, tooltips, and gaps
    const channelDots = contentEl.querySelectorAll(".w-2.h-2, .w-3.h-3"); // Channel indicator dots
    const statusIcons = contentEl.querySelectorAll(
      "svg[class*='h-4'][class*='w-4']",
    ); // Status icons
    const infoIcons = contentEl.querySelectorAll(
      "svg[class*='h-3'][class*='w-3']",
    ); // Info icons

    if (channelDots.length > 0 || statusIcons.length > 0) {
      // This is likely a Channel or Status cell
      const textElement = contentEl.querySelector(
        "span:not([class*='sr-only'])",
      );
      if (textElement?.textContent) {
        // Measure text content
        const computedStyle = window.getComputedStyle(textElement);
        context.font = `${computedStyle.fontWeight} ${computedStyle.fontSize} ${computedStyle.fontFamily}`;
        const metrics = context.measureText(textElement.textContent);
        const textWidth = Math.ceil(metrics.width);

        // Calculate additional space for visual elements
        let extraSpace = 0;

        if (channelDots.length > 0) {
          // Channel cell: dot(s) + gaps
          extraSpace = channelDots.length > 1 ? 40 : 24; // Multiple channels need more space
        } else if (statusIcons.length > 0) {
          // Status cell: icon(s) + gaps
          extraSpace = statusIcons.length > 1 ? 40 : 24; // Multiple statuses need more space
        }

        // Add space for info icon if present
        if (infoIcons.length > 0) {
          extraSpace += 20; // Info icon + gap
        }

        bestWidth = textWidth + extraSpace;
      }
    } else if (cellId === "people") {
      // Handle People column directly - has flex container with spans for names and +X counts
      const peopleContainer = contentEl.querySelector(
        "div.flex.items-center.gap-1.min-w-0",
      );

      if (peopleContainer) {
        // Measure all spans in the people container (both names and +X counts)
        const allSpans = peopleContainer.querySelectorAll("span");
        let totalTextWidth = 0;

        allSpans.forEach((span) => {
          if (span.textContent?.trim()) {
            const computedStyle = window.getComputedStyle(span);
            context.font = `${computedStyle.fontWeight} ${computedStyle.fontSize} ${computedStyle.fontFamily}`;
            const metrics = context.measureText(span.textContent.trim());
            totalTextWidth += Math.ceil(metrics.width);
          }
        });

        // Add space for gaps and padding (gap-1 = 4px between elements + padding)
        const peopleLayoutSpace = allSpans.length > 1 ? 12 : 8;
        bestWidth = totalTextWidth + peopleLayoutSpace;
      }
    }

    // If not a special cell or no width calculated yet, use standard complex content logic
    if (bestWidth === 0) {
      // Check for inline-flex elements directly (like external links)
      const inlineFlexEl = contentEl.querySelector(".inline-flex");
      if (inlineFlexEl) {
        bestWidth = Math.max(bestWidth, inlineFlexEl.scrollWidth);
      }

      // Check for tooltip triggers
      const tooltipTrigger = contentEl.querySelector(
        "[data-radix-tooltip-trigger]",
      );
      if (tooltipTrigger) {
        bestWidth = Math.max(bestWidth, tooltipTrigger.scrollWidth);
      }

      // Check all anchor tags (for external links)
      const anchors = contentEl.querySelectorAll("a");
      anchors.forEach((anchor) => {
        // For anchor tags, also measure the text content directly using canvas
        // to avoid issues with CSS constraints like overflow-hidden, text-ellipsis
        if (anchor.textContent) {
          const computedStyle = window.getComputedStyle(anchor);
          context.font = `${computedStyle.fontWeight} ${computedStyle.fontSize} ${computedStyle.fontFamily}`;
          const metrics = context.measureText(anchor.textContent);
          const textWidth = Math.ceil(metrics.width);

          // Check if anchor has padding by examining computed styles
          const paddingLeft = parseFloat(computedStyle.paddingLeft) || 0;
          const paddingRight = parseFloat(computedStyle.paddingRight) || 0;
          const anchorPadding = paddingLeft + paddingRight;

          bestWidth = Math.max(
            bestWidth,
            textWidth + anchorPadding,
            anchor.scrollWidth,
          );
        } else {
          bestWidth = Math.max(bestWidth, anchor.scrollWidth);
        }
      });

      // Fallback to container scrollWidth only if we didn't find specific content
      if (bestWidth === 0) {
        bestWidth = contentEl.scrollWidth;
      }
    }

    return bestWidth + 24; // Add padding
  }

  if (!contentEl.textContent) {
    return 0;
  }

  const text = contentEl.textContent;

  // Handle long text content differently
  if (text.length > 100) {
    return Math.min(maxWidth, Math.max(0, longContentWidth));
  }

  // For simple text content, use canvas measurement
  const computedStyle = window.getComputedStyle(contentEl);
  context.font = `${computedStyle.fontWeight} ${computedStyle.fontSize} ${computedStyle.fontFamily}`;

  const metrics = context.measureText(text);
  return Math.ceil(metrics.width) + 16; // Minimal padding
}

/**
 * Creates a reusable column resize handler for table components
 * @param setColumnSizing Function to update column sizing state
 * @param tableId The unique table identifier
 * @param options Configuration options for the resize calculation
 * @param onComplete Optional callback after resize is complete
 * @returns A resize handler function
 */
export function createColumnResizeHandler(
  setColumnSizing: (
    updater: (prev: Record<string, number>) => Record<string, number>,
  ) => void,
  tableId: string,
  options: ColumnResizeOptions = {},
  onComplete?: () => void,
) {
  return (_: MouseEvent, columnId: string) => {
    const optimalWidth = calculateColumnWidth(columnId, tableId, options);

    setColumnSizing((prev) => ({
      ...prev,
      [columnId]: optimalWidth,
    }));

    onComplete?.();
  };
}

/**
 * Creates a single column resize handler (without MouseEvent)
 * @param setColumnSizing Function to update column sizing state
 * @param tableId The unique table identifier
 * @param options Configuration options for the resize calculation
 * @param onComplete Optional callback after resize is complete
 * @returns A resize handler function that takes only columnId
 */
export function createSingleColumnResizeHandler(
  setColumnSizing: (
    updater: (prev: Record<string, number>) => Record<string, number>,
  ) => void,
  tableId: string,
  options: ColumnResizeOptions = {},
  onComplete?: () => void,
) {
  return (columnId: string) => {
    const optimalWidth = calculateColumnWidth(columnId, tableId, options);

    setColumnSizing((prev) => ({
      ...prev,
      [columnId]: optimalWidth,
    }));

    onComplete?.();
  };
}

/**
 * Creates a bulk resize handler for all columns in a table
 * @param setColumnSizing Function to update column sizing state
 * @param tableId The unique table identifier
 * @param options Configuration options for the resize calculation
 * @param onComplete Optional callback after resize is complete
 * @returns A function that resizes all provided column IDs
 */
export function createBulkColumnResizeHandler(
  setColumnSizing: (
    updater: (prev: Record<string, number>) => Record<string, number>,
  ) => void,
  tableId: string,
  options: ColumnResizeOptions = {},
  onComplete?: () => void,
) {
  return (columnIds: string[]) => {
    const newSizing: Record<string, number> = {};

    // Calculate optimal width for each column
    columnIds.forEach((columnId) => {
      const optimalWidth = calculateColumnWidth(columnId, tableId, options);
      newSizing[columnId] = optimalWidth;
    });

    // Update all column sizes at once
    setColumnSizing((prev) => ({
      ...prev,
      ...newSizing,
    }));

    onComplete?.();
  };
}
